import React from 'react'

const services = [
  {
    icon: 'web',
    title: 'Web Development',
    description: 'Fast, accessible websites and web apps that turn your ideas into useful digital experiences.',
    tags: ['React', 'Responsive', 'Scalable'],
  },
  {
    icon: 'laptop',
    title: 'Laptop Services',
    description: 'Friendly setup, troubleshooting, and maintenance to keep your computers secure and running smoothly.',
    tags: ['Setup', 'Repairs', 'Optimization'],
  },
  {
    icon: 'design',
    title: 'IT Consulting',
    description: 'Clear interfaces and thoughtful user journeys that make every interaction feel effortless.',
    tags: ['Research', 'Prototyping', 'Design systems'],
  },
  {
    icon: 'email',
    title: 'Business Email',
    description: 'Professional email setup and support that keeps your team connected, organized, and credible.',
    tags: ['Setup', 'Migration', 'Security'],
  },
 
  {
    icon: 'cloud',
    title: 'Cloud & DevOps',
    description: 'Secure infrastructure and smoother deployments that help your team deliver with confidence.',
    tags: ['Cloud', 'Automation', 'Security'],
  },
 
  {
    icon: 'support',
    title: 'Support & Maintenance',
    description: 'Ongoing improvements, monitoring, and support to keep your digital presence dependable.',
    tags: ['Monitoring', 'Updates', 'Care plans'],
  },
]

function ServiceIcon({ type }) {
  const commonProps = {
    className: 'h-6 w-6',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }

  if (type === 'web') {
    return <svg {...commonProps}><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M3 8h18M7 20h10M9 18v2M15 18v2" /><path d="M7 6h.01M10 6h.01" /></svg>
  }

  if (type === 'laptop') {
    return <svg {...commonProps}><rect x="5" y="4" width="14" height="10" rx="1.5" /><path d="M3 18h18M7 18l1-4h8l1 4" /></svg>
  }

  if (type === 'design') {
    return <svg {...commonProps}><path d="m14 6 4 4M4 20l4.2-1 9.1-9.1a2.8 2.8 0 0 0-4-4L4.2 15 4 20Z" /><path d="m12 8 4 4M7 16h.01" /></svg>
  }

  if (type === 'mobile') {
    return <svg {...commonProps}><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M10 5h4M11 18.5h2" /></svg>
  }

  if (type === 'cloud') {
    return <svg {...commonProps}><path d="M7 18h10a4 4 0 0 0 .8-7.9A6 6 0 0 0 6.1 8.4 4.8 4.8 0 0 0 7 18Z" /><path d="m12 10-2 3h2l-1 3 3-4h-2l1-2Z" /></svg>
  }

  if (type === 'email') {
    return <svg {...commonProps}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>
  }

  if (type === 'strategy') {
    return <svg {...commonProps}><circle cx="12" cy="12" r="8.5" /><path d="m15.5 8.5-2.1 4.9-4.9 2.1 2.1-4.9 4.9-2.1Z" /><circle cx="12" cy="12" r="1" /></svg>
  }

  return <svg {...commonProps}><circle cx="12" cy="12" r="8.5" /><path d="M8.5 12.5 11 15l4.5-5" /></svg>
}

function Services() {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="absolute -right-24 top-16 h-64 w-64 rounded-full bg-cyan-200/40 blur-3xl dark:bg-cyan-950/30" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">What we do</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Technology with a purpose.
          </h1>
          <p className="mt-5 text-base leading-7 text-slate-600 dark:text-slate-300">
            From the first sketch to long-term support, we bring the strategy, design, and engineering your next step needs.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <article
              key={service.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-900/10 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-cyan-700"
            >
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700 transition-colors group-hover:bg-cyan-400 group-hover:text-[#071a3d] dark:bg-cyan-950/50 dark:text-cyan-300">
                  <ServiceIcon type={service.icon} />
                </span>
                <span className="text-3xl font-light text-slate-200 transition group-hover:text-cyan-300 dark:text-slate-700">-&gt;</span>
              </div>
              <h2 className="mt-8 text-xl font-bold text-slate-900 dark:text-white">{service.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">{service.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {tag}
                  </span>
                ))}
              </div>
              <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-cyan-400 transition-transform duration-300 group-hover:scale-x-100" />
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Services
